'use client'

import * as React from 'react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/sotano-kit/primitives/dialog'
import { useToggleState } from '~/hooks/use-toggle-state'
import { Button } from '~/sotano-kit/primitives/button'
import { toast } from 'sonner'
import { reRollAPIToken } from './action'
import ph from 'posthog-js'
import * as va from '@vercel/analytics'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'

export function ReRollAPITokenForm({
  token,
  tokenName,
}: {
  token: string
  tokenName: string
}) {
  const toggleState = useToggleState()
  const [loading, setLoading] = React.useState(false)
  const { data } = useSelectedTeamAndRepo()
  const replicache = useRepoReplicache().data?.replicache

  return (
    <>
      <Button
        onClick={toggleState.handleOn}
        className="text-xs text-dark-bright whitespace-nowrap"
        intent="secondary"
        size="xs"
      >
        Re-Roll
      </Button>
      <Dialog open={toggleState.isOn} onOpenChange={toggleState.handleToggle}>
        <DialogContent asChild>
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              if (!data?.team || !data.repo) {
                throw new Error('team or repo not loaded.')
              }
              try {
                setLoading(true)
                const result = await reRollAPIToken({
                  teamId: data.team.id,
                  repoId: data.repo.id,
                  token,
                })

                if (result.error) {
                  throw new Error(result.error)
                }

                va.track('Token Re-Rolled', {
                  teamId: data.team.id,
                  repoId: data.repo.id,
                })
                ph.capture('Token Re-Rolled', {
                  teamId: data.team.id,
                  repoId: data.repo.id,
                })

                await replicache?.pull()

                toggleState.handleOff()
                toast.success('Token re-rolled successfully!')
              } catch (error) {
                console.error(error)
                toast.error('Error re-rolling token.')
              } finally {
                setLoading(false)
              }
            }}
          >
            <DialogHeader>
              <DialogTitle>
                Are you sure you want to re-roll your {tokenName} Token?
              </DialogTitle>
              <DialogDescription>
                This will immediately revoke the current API Token, making it
                unauthorized to access all BaseHub APIs. All connections that
                rely on this token will stop working.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter divider>
              <DialogClose asChild className="self-end">
                <Button intent="secondary">Cancel</Button>
              </DialogClose>
              <Button disabled={loading} className="whitespace-nowrap">
                {loading ? 'Submitting...' : 'Re-Roll'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
